import type { InjectionKey, Ref } from "vue";
import { inject, provide, ref } from "vue";
import {
	fetchSquareMobileIndexData,
	fetchSquarePcIndexData,
	type SquareMobileIndexData,
	type SquarePcIndexData,
} from "~/utils/page-data/square";

const SquarePcPageDataKey: InjectionKey<Ref<SquarePcIndexData | null>> = Symbol("squarePcPageData");
const SquareMobilePageDataKey: InjectionKey<Ref<SquareMobileIndexData | null>> =
	Symbol("squareMobilePageData");

export function useSquarePageData(platform: "pc" | "mobile") {
	const dataKey = platform === "pc" ? "square-pc-index-data" : "square-mobile-index-data";

	const { data, refresh, pending, error } = useLazyAsyncData<
		SquarePcIndexData | SquareMobileIndexData
	>(dataKey, () => (platform === "pc" ? fetchSquarePcIndexData() : fetchSquareMobileIndexData()), {
		default: () =>
			platform === "pc"
				? ({
						recommend: { featuredLeft: null, podcastTopSix: [] },
						shorts: [],
						categories: [],
						focusList: [],
						focusTotal: 0,
					} satisfies SquarePcIndexData)
				: ({
						media: {},
						imageTextList: [],
						recommend: { featuredLeft: null, podcastTopSix: [] },
						shorts: [],
					} satisfies SquareMobileIndexData),
	});

	if (platform === "pc") {
		provide(SquarePcPageDataKey, data as Ref<SquarePcIndexData | null>);
	} else {
		provide(SquareMobilePageDataKey, data as Ref<SquareMobileIndexData | null>);
	}

	return {
		data,
		refresh,
		pending,
		error,
	};
}

export function useSquarePcPageData() {
	return inject(SquarePcPageDataKey, ref(null));
}

export function useSquareMobilePageData() {
	return inject(SquareMobilePageDataKey, ref(null));
}
